/**
 * Switch Field Type
 *
 * Provides configuration and renderers for switch (toggle) fields:
 * - Read-only column renderer (disabled switch)
 * - Editable column renderer (interactive switch)
 */

import type { ColumnDef, CellContext } from '@tanstack/react-table';
import { Switch } from '@mui/material';
import { t } from '@lingui/macro';
import { AccessControl } from 'features/accessControl';
import type { FieldTypeConfig, ColumnRenderer, RenderOptions, CustomColumn } from '../../types';
import { S } from './columnWidths';

// ============================================================================
// Read-Only Column Renderer
// ============================================================================

/**
 * Switch read-only renderer - renders a disabled toggle switch
 * Uses switchConfig for checked state
 */
export const switchReadOnlyRenderer = <T extends { id: number | string }>(
  col: CustomColumn<T>,
  _options: RenderOptions<T>
): Partial<ColumnDef<T>> => {
  if (!col.switchConfig) {
    throw new Error(`switchConfig required for switch column: ${col.id}`);
  }

  const { checked } = col.switchConfig;

  return {
    cell: ({ row }: CellContext<T, unknown>) => {
      const isChecked = checked(row.original);
      return <Switch checked={isChecked} disabled size="small" />;
    },
    tooltipRender: (cell: CellContext<T, unknown>) => {
      const isChecked = checked(cell.row.original);
      return isChecked ? t`Active` : t`Inactive`;
    },
    enableSorting: false,
  };
};

// ============================================================================
// Editable Column Renderer
// ============================================================================

/**
 * Switch editable renderer - renders an interactive toggle switch
 * Uses switchConfig for checked/onChange/requiresAccess
 *
 * Usage in column config:
 * ```tsx
 * status: {
 *   id: 'status',
 *   label: 'Status',
 *   type: 'switch',
 *   editable: true,
 *   switchConfig: {
 *     checked: (row) => row.status === 'Active',
 *     onChange: (row) => toggleStatus(row),
 *     requiresAccess: { scope: '/settings', write: true },
 *   },
 * }
 * ```
 */
export const switchEditableRenderer = <T extends { id: number | string }>(
  col: CustomColumn<T>,
  _options: RenderOptions<T>
): Partial<ColumnDef<T>> => {
  if (!col.switchConfig) {
    throw new Error(`switchConfig required for switch column: ${col.id}`);
  }

  const { checked, onChange, requiresAccess } = col.switchConfig;

  return {
    cell: ({ row }: CellContext<T, unknown>) => {
      const isChecked = checked(row.original);

      const switchElement = (
        <Switch checked={isChecked} onChange={() => onChange(row.original)} size="small" />
      );

      if (requiresAccess) {
        return (
          <AccessControl
            write={requiresAccess.write}
            scope={requiresAccess.scope}
            fallback={<Switch checked={isChecked} disabled size="small" />}
          >
            {switchElement}
          </AccessControl>
        );
      }

      return switchElement;
    },
    tooltipRender: (cell: CellContext<T, unknown>) => {
      const isChecked = checked(cell.row.original);
      return isChecked ? t`Active` : t`Inactive`;
    },
    enableSorting: false,
  };
};

// ============================================================================
// Field Type Configuration
// ============================================================================

/**
 * Switch field type configuration
 */
export const switchFieldType: FieldTypeConfig = {
  placeholder: '',
  ...S,
};

// ============================================================================
// Typed Renderer Exports (for registry)
// ============================================================================

/** Read-only renderer with proper typing */
export const switchReadOnly: ColumnRenderer = switchReadOnlyRenderer;

/** Editable renderer with proper typing */
export const switchEditable: ColumnRenderer = switchEditableRenderer;
